// ADR-133 D11 (Unmapped in last run tab): job titles the latest complete real
// Employees sync over the WHOLE population could not resolve to a position, plus
// titles seen by scoped runs since (e.g. a bench candidate import), with a
// one-click "Map this title". Titles that now have an exact mapping are struck
// through. The run facts sit in the page header (UnmappedRunStrip).
import React from 'react';
import {
  Alert, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { titleStatusOf, TITLE_STATUSES } from './unmappedTitleStatus';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { SYNC_RUN_ORIGIN_LABELS } from '../../../constants/ingestionSyncRuns';
import { PatternCell, MutedText } from './styles';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '');

/** "Last full run" and/or "Bench candidate import · 05/10/2026, 17:42 · ops@x.com" (latest scoped run). */
const describeSeenIn = (entry, scopedById) => {
  const parts = [];
  if (entry.inLastFullRun) {
    parts.push('Last full run');
  }
  const latestScoped = (entry.seenInScopedRuns || [])
    .map((runId) => scopedById.get(runId))
    .filter(Boolean)
    .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))[0];
  if (latestScoped) {
    const label = SYNC_RUN_ORIGIN_LABELS[latestScoped.origin] || 'Scoped run';
    parts.push([label, fmt(latestScoped.startedAt), latestScoped.triggeredByUser].filter(Boolean).join(' · '));
  }
  return parts.join(' + ');
};

const STATUS_WARNING_COLOR = '#B45309';

/** Why a mapping that existed before the run did not apply (shown in a tooltip). */
const notAppliedReason = (entry) => (entry.latestRunMappingSource && entry.latestRunMappingSource !== 'radar-api'
  ? `That run read the "${entry.latestRunMappingSource}" mappings, not the ones saved here. Set JOB_POSITION_MAPPING_SOURCE=radar-api in radar-ingestion.`
  : 'The mapping existed when the run started, yet the title still did not resolve. Check that the mapped position exists in Radar (it may have been renamed or removed).');

const UnmappedTitlesPanel = ({
  unmapped, providerLabel, onMapTitle, onShowMapping,
}) => {
  const ability = useAbility();
  const canManage = ability.can(ACTIONS.MANAGE, SUBJECTS.INGESTION_CONFIGURATION);
  const {
    data, loading, titles, versionChanged, hasRun, scopedRuns,
  } = unmapped;
  const scopedById = new Map((scopedRuns || []).map((run) => [run.runId, run]));

  if (loading && !data) {
    return <LoadingSpinner />;
  }
  if (!hasRun) {
    return (
      <EmptyState
        title="No recorded run yet"
        description={`Titles appear here after the next complete real ${providerLabel} employees sync is recorded.`}
      />
    );
  }
  if (titles.length === 0) {
    return <EmptyState title="Every title resolved" description="The last run had no unmapped job titles." />;
  }

  return (
    <>
      {versionChanged && (
        <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
          {`Mappings changed since that run (now v${data.currentMappingSetVersion}). Titles may already resolve through a glob added since — the next sync will tell.`}
        </Alert>
      )}
      <div style={{ marginTop: 16 }}>
        <ResultsCount count={titles.length} singular="unmapped title" plural="unmapped titles" />
      </div>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Job title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Seen in</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {titles.map((entry) => {
              const { title } = entry;
              const status = titleStatusOf(entry);
              return (
              <TableRow key={title} hover>
                <TableCell>
                  <PatternCell style={status === TITLE_STATUSES.MAPPED_SINCE ? { textDecoration: 'line-through', color: '#94A3B8' } : undefined}>
                    {title}
                  </PatternCell>
                </TableCell>
                <TableCell>
                  {status === TITLE_STATUSES.MAPPED_NOT_APPLIED ? (
                    <Tooltip arrow title={notAppliedReason(entry)}>
                      <MutedText style={{ color: STATUS_WARNING_COLOR, cursor: 'help' }}>
                        Mapped before this run, still unresolved
                      </MutedText>
                    </Tooltip>
                  ) : (
                    <MutedText>
                      {status === TITLE_STATUSES.MAPPED_SINCE ? 'Mapped — applies from the next sync' : 'Not mapped'}
                    </MutedText>
                  )}
                </TableCell>
                <TableCell>
                  <MutedText>{describeSeenIn(entry, scopedById)}</MutedText>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {canManage && status === TITLE_STATUSES.NOT_MAPPED && (
                    <Tooltip title="Map this title">
                      <IconButton size="small" onClick={() => onMapTitle(title)}>
                        <AddLinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {status === TITLE_STATUSES.MAPPED_NOT_APPLIED && onShowMapping && (
                    <Tooltip title="Show the mapping">
                      <IconButton size="small" onClick={() => onShowMapping(title)}>
                        <ManageSearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default UnmappedTitlesPanel;
